import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import biApi from "../../../../../../api/biApi.ts";
import type { NumberFormatterOptions } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  AccountingBook,
  BalanceSheetDataResponse,
  BalanceSheetIncomeBalanceType,
  BcDimensionBreakdownBatch,
  DimensionDescriptor,
  DimensionDictionary,
} from "../../../../../../api/types/svTypes.ts";
import useDeferredRequest from "../../../hooks/useDeferredRequest.ts";
import { useAppDispatch } from "../../../sv/store/hooks/useAppDispatch.ts";
import {
  selectCacheSessionId,
  selectRefreshToken,
  selectSessionId,
} from "../../../sv/store/selectors/currentReportSelectors.ts";
import { selectLogQueries } from "../../../sv/store/selectors/devToolsSelectors.ts";
import {
  selectCurrentDataSetId,
  selectDimensions,
  selectDimensionsStructure,
  selectMeasures,
} from "../../../sv/store/selectors/metaDataSelectors.ts";
import { createCurrentReportAction, createDevToolsAction } from "../../../sv/store/utils/storeUtils.ts";
import { DataSetIds } from "../../../sv/utilities/DimensionNames.ts";
import type { BalanceSheetEntityOption } from "../utils/balanceSheetEntityFilter.ts";
import { getBalanceSheetEntityFilter } from "../utils/balanceSheetEntityFilter.ts";
import type { BalanceSheetPeriodState, BalanceSheetPriorPeriodType } from "../utils/balanceSheetPeriod.ts";
import {
  createDefaultBalanceSheetPeriodState,
  getBalanceSheetPeriodRange,
  setBalanceSheetPriorPeriodCount,
  setBalanceSheetPriorPeriodType,
} from "../utils/balanceSheetPeriod.ts";
import { buildBalanceSheetRequest } from "../utils/buildBalanceSheetRequest.ts";
import type { BalanceSheetPreloadedProps } from "../withBalanceSheetPreload.tsx";
import useBalanceSheetData from "./useBalanceSheetData.ts";
import { useBalanceSheetExport } from "./useBalanceSheetExport.ts";

const errorMessageAutoHideDuration = 5000;
const BC_DIMENSION_PREFIX = "BcDimension_";
const NAME_DIMENSION_SUFFIX = "_name";

const getBcDimensions = (dimensions: DimensionDescriptor[]): DimensionDescriptor[] =>
  dimensions
    .filter((d) => d.name.startsWith(BC_DIMENSION_PREFIX) && !d.name.endsWith(NAME_DIMENSION_SUFFIX))
    .map((d) => ({ ...d, caption: d.caption.replace(/ Code$/, "") }));

interface UseBalanceSheetStateParams {
  reportId: string;
  incomeBalanceType: BalanceSheetIncomeBalanceType;
  settings: BalanceSheetPreloadedProps["settings"];
  entityOptions: BalanceSheetEntityOption[];
}

export interface BalanceSheetState {
  // Report data
  data: BalanceSheetDataResponse | undefined;
  error: unknown;
  isFetching: boolean;
  isInitialLoading: boolean;
  isTableRefreshing: boolean;
  refresh: () => void;

  // Redux selectors (needed by BiCommonDataContextProvider)
  dataSetId: string;
  dimensions: DimensionDescriptor[];
  dimensionsStructure: ReturnType<typeof selectDimensionsStructure>;
  measures: ReturnType<typeof selectMeasures>;
  refreshToken: string | undefined;

  // Accounting book
  books: AccountingBook[];
  isMultiBook: boolean;
  selectedAccountingBook: string | null;
  onAccountingBookChange: (value: string | null) => void;

  // Period
  period: BalanceSheetPeriodState;
  onPeriodChange: (period: BalanceSheetPeriodState) => void;
  onPeriodTypeChange: (periodType: BalanceSheetPriorPeriodType) => void;
  onPeriodCountChange: (periodCount: number) => void;

  // Entity
  entityOptions: BalanceSheetEntityOption[];
  selectedEntity: BalanceSheetEntityOption | null;
  onEntityChange: (entity: BalanceSheetEntityOption | null) => void;

  // Number format
  numberFormat: NumberFormatterOptions | undefined;
  onNumberFormatChange: (numberFormat: NumberFormatterOptions | undefined) => void;

  // BC dimension breakdown
  bcDimensions: DimensionDescriptor[];
  selectedBreakDownDimensionName: string | undefined;
  onBreakDownDimensionChange: (value: string | undefined) => void;
  dimensionValues: DimensionDictionary[];
  isDimensionValuesLoading: boolean;
  selectedDimensionValues: string[];
  onSelectedDimensionValuesChange: (values: string[]) => void;
  hideOtherDimension: boolean;
  onHideOtherDimensionChange: (value: boolean) => void;
  hideUnassignedDimension: boolean;
  onHideUnassignedDimensionChange: (value: boolean) => void;
  hasDimensionBreakdown: boolean;
  isSettingsDefault: boolean;
  onResetSettings: () => void;

  // Build progress
  buildProgress: number | undefined;

  // Export
  exporting: boolean;
  exportValuesAsDisplayed: boolean;
  onExportValuesAsDisplayedChange: (value: boolean) => void;
  onExport: () => void;
  onExportWithParameters: () => void;
}

const useBalanceSheetState = ({
  reportId,
  incomeBalanceType,
  settings,
  entityOptions,
}: UseBalanceSheetStateParams): BalanceSheetState => {
  const dispatch = useAppDispatch();
  const { sendNotificationError } = useNotificationContext();

  const dataSetId = useSelector(selectCurrentDataSetId) ?? DataSetIds.GeneralAndMemo;
  const dimensions = useSelector(selectDimensions);
  const dimensionsStructure = useSelector(selectDimensionsStructure);
  const measures = useSelector(selectMeasures);
  const sessionId = useSelector(selectSessionId);
  const cacheSessionId = useSelector(selectCacheSessionId);
  const refreshToken = useSelector(selectRefreshToken);
  const isQueryLoggingActive = useSelector(selectLogQueries) === true;

  // User-controlled state
  const [selectedAccountingBook, setSelectedAccountingBook] = useState<string | null>(null);
  const [period, setPeriod] = useState<BalanceSheetPeriodState>(createDefaultBalanceSheetPeriodState);
  const [selectedEntity, setSelectedEntity] = useState<BalanceSheetEntityOption | null>(null);
  const [exportValuesAsDisplayed, setExportValuesAsDisplayed] = useState(false);
  const [numberFormat, setNumberFormat] = useState<NumberFormatterOptions>();
  const [selectedBreakDownDimensionName, setSelectedBreakDownDimensionName] = useState<string | undefined>();
  const [selectedDimensionValues, setSelectedDimensionValues] = useState<string[]>([]);
  const [hideOtherDimension, setHideOtherDimension] = useState(false);
  const [hideUnassignedDimension, setHideUnassignedDimension] = useState(false);

  // BC dimension values
  const loadDimensionValues = useCallback(
    (signal?: AbortSignal) =>
      selectedBreakDownDimensionName
        ? biApi.getBalanceSheetDimensionValues(selectedBreakDownDimensionName, signal)
        : Promise.resolve({ success: true as const, data: [] as DimensionDictionary[] }),
    [selectedBreakDownDimensionName]
  );
  const [dimensionValuesData, , { isFetching: isDimensionValuesLoading }] =
    useFetch<DimensionDictionary[]>(loadDimensionValues);
  const dimensionValues = useMemo(() => dimensionValuesData ?? [], [dimensionValuesData]);

  // BC dimension breakdown
  const handleBreakDownDimensionChange = useCallback((value: string | undefined) => {
    setSelectedBreakDownDimensionName(value);
    setSelectedDimensionValues([]);
    setHideOtherDimension(false);
    setHideUnassignedDimension(false);
  }, []);

  const handleResetSettings = useCallback(() => {
    setNumberFormat(undefined);
    setSelectedBreakDownDimensionName(undefined);
    setSelectedDimensionValues([]);
    setHideOtherDimension(false);
    setHideUnassignedDimension(false);
  }, []);

  const bcDimensions = useMemo(() => getBcDimensions(dimensions), [dimensions]);

  // Accounting book
  const books = useMemo<AccountingBook[]>(() => settings?.accountingBookSettings?.books ?? [], [settings]);
  const isMultiBook = settings?.accountingBookSettings?.multiBook === true;

  useEffect(() => {
    if (!isMultiBook || books.length === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setSelectedAccountingBook(null);
      return;
    }

    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setSelectedAccountingBook((previous) => {
      if (previous && books.some((book) => book.code === previous)) {
        return previous;
      }
      return books[0]?.code ?? null;
    });
  }, [books, isMultiBook]);

  const effectiveAccountingBook = useMemo(() => {
    if (!isMultiBook) {
      return null;
    }
    return selectedAccountingBook ?? books[0]?.code ?? null;
  }, [books, isMultiBook, selectedAccountingBook]);

  // Derived values
  const selectedEntityFilter = useMemo(() => getBalanceSheetEntityFilter(selectedEntity), [selectedEntity]);
  const periodRange = useMemo(() => getBalanceSheetPeriodRange(period), [period]);
  const periodSettings = useMemo(
    () => ({
      priorPeriodType: period.priorPeriodType,
      priorPeriodCount: period.priorPeriodCount,
    }),
    [period.priorPeriodCount, period.priorPeriodType]
  );

  const breakdownBatches = useMemo<BcDimensionBreakdownBatch[] | undefined>(() => {
    if (!selectedBreakDownDimensionName || selectedDimensionValues.length === 0) {
      return undefined;
    }

    const dimensionName = selectedBreakDownDimensionName;
    const batches: BcDimensionBreakdownBatch[] = selectedDimensionValues.map((value) => ({
      condition: {
        dimensionName,
        equalityType: ConditionEqualityType.Equal,
        values: [value],
      },
    }));

    if (!hideOtherDimension) {
      batches.push({
        condition: {
          dimensionName,
          equalityType: ConditionEqualityType.NotEqual,
          values: [...selectedDimensionValues, null],
        },
      });
    }

    if (!hideUnassignedDimension) {
      batches.push({
        condition: {
          dimensionName,
          equalityType: ConditionEqualityType.Equal,
          values: [null],
        },
      });
    }

    return batches;
  }, [selectedBreakDownDimensionName, selectedDimensionValues, hideOtherDimension, hideUnassignedDimension]);

  const hasDimensionBreakdown = breakdownBatches !== undefined && breakdownBatches.length > 0;
  const isSettingsDefault = numberFormat === undefined && selectedBreakDownDimensionName === undefined;

  // Build request
  const buildRequest = useMemo(
    () =>
      buildBalanceSheetRequest(
        sessionId,
        cacheSessionId ?? "",
        reportId,
        incomeBalanceType,
        effectiveAccountingBook,
        periodRange,
        periodSettings,
        numberFormat,
        isQueryLoggingActive,
        selectedEntityFilter,
        breakdownBatches
      ),
    [
      sessionId,
      cacheSessionId,
      reportId,
      incomeBalanceType,
      effectiveAccountingBook,
      periodRange,
      periodSettings,
      numberFormat,
      isQueryLoggingActive,
      selectedEntityFilter,
      breakdownBatches,
    ]
  );

  // Deferred request (debounce non-initial changes by 2.5s with progress indicator)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { deferredValue: deferredRequest, buildProgress } = useDeferredRequest(buildRequest, hasLoadedOnce);

  // Data fetching
  const { data, error, isFetching, refresh } = useBalanceSheetData(deferredRequest);

  useEffect(() => {
    if (!error) {
      return;
    }

    const errorMessage =
      typeof error.message === "string" && error.message.length > 0
        ? error.message
        : "An error occurred while calculating report data. Please try again.";
    sendNotificationError(errorMessage, errorMessageAutoHideDuration);
  }, [error, sendNotificationError]);

  useEffect(() => {
    if (data && !hasLoadedOnce) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setHasLoadedOnce(true);
    }
  }, [data, hasLoadedOnce]);
  const { exporting, requestExportWithoutParameters, requestExportWithParameters } = useBalanceSheetExport(
    deferredRequest,
    dimensions,
    exportValuesAsDisplayed
  );

  // Side effects
  useEffect(() => {
    dispatch(createCurrentReportAction("setEtag", { etag: data?.etag, cacheValid: undefined }));
  }, [dispatch, data?.etag]);

  useEffect(() => {
    dispatch(createDevToolsAction("update", { loggingItems: data?.loggingItems }));
  }, [dispatch, data?.loggingItems]);

  useEffect(() => {
    if (!isQueryLoggingActive) {
      dispatch(createDevToolsAction("update", { loggingItems: undefined }));
    }
  }, [dispatch, isQueryLoggingActive]);

  const isInitialLoading = isFetching && !data;
  const isTableRefreshing = isFetching && !!data;

  const handlePeriodTypeChange = useCallback(
    (periodType: BalanceSheetPriorPeriodType) =>
      setPeriod((current) => setBalanceSheetPriorPeriodType(current, periodType)),
    []
  );

  const handlePeriodCountChange = useCallback(
    (periodCount: number) => setPeriod((current) => setBalanceSheetPriorPeriodCount(current, periodCount)),
    []
  );

  return {
    data,
    error,
    isFetching,
    isInitialLoading,
    isTableRefreshing,
    refresh,
    dataSetId,
    dimensions,
    dimensionsStructure,
    measures,
    refreshToken,
    books,
    isMultiBook,
    selectedAccountingBook,
    onAccountingBookChange: setSelectedAccountingBook,
    period,
    onPeriodChange: setPeriod,
    onPeriodTypeChange: handlePeriodTypeChange,
    onPeriodCountChange: handlePeriodCountChange,
    entityOptions,
    selectedEntity,
    onEntityChange: setSelectedEntity,
    numberFormat,
    onNumberFormatChange: setNumberFormat,
    bcDimensions,
    selectedBreakDownDimensionName,
    onBreakDownDimensionChange: handleBreakDownDimensionChange,
    dimensionValues,
    isDimensionValuesLoading,
    selectedDimensionValues,
    onSelectedDimensionValuesChange: setSelectedDimensionValues,
    hideOtherDimension,
    onHideOtherDimensionChange: setHideOtherDimension,
    hideUnassignedDimension,
    onHideUnassignedDimensionChange: setHideUnassignedDimension,
    hasDimensionBreakdown,
    isSettingsDefault,
    onResetSettings: handleResetSettings,
    buildProgress,
    exporting,
    exportValuesAsDisplayed,
    onExportValuesAsDisplayedChange: setExportValuesAsDisplayed,
    onExport: requestExportWithoutParameters,
    onExportWithParameters: requestExportWithParameters,
  };
};

export default useBalanceSheetState;
